/**
 * The design system's form controls (issue #189, ADR-0020): every text, number, money, date,
 * select, checkboxControl, radio, switch and textarea of the console and the public pages is a Web
 * Awesome web component (`<wa-input>`, `<wa-select>`…) themed with Nubi's tokens
 * (`controls.css`). This module only builds their markup (escaped, like every view); the
 * components are defined, and their accessibility wired, by `controls-define.ts`, which each
 * page's entry point imports. Files go through `file-picker.ts`.
 *
 * Only this module and `file-picker.ts` write a bare `<input>`, `<select>` or `<textarea>`
 * (the honeypot below, the native file input): `test/controls.test.ts` keeps it that way.
 *
 * The components are form-associated: same id, name, value, required and validation as the
 * native control, `new FormData(form)` and `form.elements` see them, and they fire `input` and
 * `change` from the element itself, so the pages read and listen to them as before. What
 * differs: the label goes inside (`label`, so the inner control is named), `:checked` is
 * `.checked` (use `checkedValues`), and a select with no `selected` option starts on its first
 * option, as a native one does (`selectControl` marks it).
 */

import { guatemalaMonth, period } from './console/format.ts';

/** An attribute's value: text, a number, or a boolean attribute (true: present). */
export type AttrValue = string | number | boolean | null | undefined;
/** Attributes as written in HTML (`id`, `data-plan`, `aria-describedby`…), escaped here. */
export type Attrs = Record<string, AttrValue>;

/** What every labelled control takes besides its attributes. */
export interface Labelled {
  /** The visible label (plain text). Required unless `field()` gives it. */
  label?: string;
  /** Keep the label for screen readers only (a control inside a table cell). */
  hideLabel?: boolean;
  /** A line under the control. */
  hint?: string;
  /** A label with markup (escaped by the caller), e.g. a required mark: goes in the label slot. */
  labelHtml?: string;
}

export const esc = (s: string) =>
  s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );

const unesc = (s: string) =>
  s
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');

/**
 * The mark of a field the person must fill (#235, UI UX Pro Max §8 `required-indicators`): an
 * asterisk in sight (hidden from screen readers) and «(obligatorio)» for them. The form says
 * once «Los campos con * son obligatorios.» (`REQUIRED_HINT`).
 */
export const REQUIRED_MARK =
  '<span class="req" aria-hidden="true">*</span><span class="sr-only"> (obligatorio)</span>';
/** A label (plain text) with the required mark, for a control's `labelHtml`. */
export const requiredLabel = (label: string) => `${esc(label)} ${REQUIRED_MARK}`;
/** The one line a form with required fields says about the mark. */
export const REQUIRED_HINT =
  '<p class="muted small req-hint">Los campos con <span class="req" aria-hidden="true">*</span> son obligatorios.</p>';

/** `{ id: 'a', required: true, maxlength: 3, hidden: false }` → ` id="a" required maxlength="3"`. */
export function attrs(a: Attrs): string {
  let out = '';
  for (const [k, v] of Object.entries(a)) {
    if (v === undefined || v === null || v === false) continue;
    out += v === true ? ` ${k}` : ` ${k}="${esc(String(v))}"`;
  }
  return out;
}

/** The label/hint attributes of a labelled control (and the class that hides the label). */
function labelAttrs(a: Attrs & Labelled): { rest: Attrs; extra: Attrs; slot: string } {
  const { label, hideLabel, hint, labelHtml, class: cls, ...rest } = a;
  const classes = [cls, hideLabel ? 'sr-label' : ''].filter(Boolean).join(' ');
  return {
    rest,
    extra: { label: labelHtml ? undefined : label, hint, class: classes || undefined },
    slot: labelHtml ? `<span slot="label">${labelHtml}</span>` : '',
  };
}

/** Text, e-mail, phone, URL, search, date or time: `<wa-input>` (a month: `monthControl`). */
export function textControl(
  a: Attrs &
    Labelled & {
      type?:
        | 'text'
        | 'email'
        | 'tel'
        | 'url'
        | 'search'
        | 'password'
        | 'number'
        | 'date'
        | 'datetime-local'
        | 'time';
    },
): string {
  const { rest, extra, slot } = labelAttrs(a);
  return `<wa-input${attrs({ ...extra, ...rest })}>${slot}</wa-input>`;
}

/** A date (ISO `YYYY-MM-DD` value, as a native date input). */
export const dateControl = (a: Attrs & Labelled) => textControl({ ...a, type: 'date' });

/**
 * A month (`YYYY-MM`): a select of months named in Spanish («Mayo de 2026»), newest first, as
 * Estadísticas offers them (#242). Not `<wa-input type="month">`: the browser paints that one in
 * its own language («May 2026»). It offers the 24 months up to `today` (Guatemala's current month
 * unless given), and the value too when it falls outside them (an old statement).
 */
export function monthControl(
  a: Attrs & Labelled & { value?: string; today?: string; months?: number },
): string {
  const { value, today = guatemalaMonth(new Date()), months = 24, ...rest } = a;
  const [y, m] = today.split('-').map(Number) as [number, number];
  const list = Array.from({ length: months }, (_, i) => {
    const t = y * 12 + (m - 1) - i;
    return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`;
  });
  if (value && /^\d{4}-\d{2}$/.test(value) && !list.includes(value)) {
    list.push(value);
    list.sort().reverse();
  }
  return selectControl(
    rest,
    list.map((p) => ({ value: p, label: period(p), selected: p === value })),
  );
}

/** A whole number with steppers (`min`, `max`, `step`): `<wa-number-input>`. */
export function numberControl(a: Attrs & Labelled & { steppers?: boolean }): string {
  const { steppers = true, ...b } = a;
  const { rest, extra, slot } = labelAttrs(b);
  return `<wa-number-input${attrs({
    inputmode: 'numeric',
    ...extra,
    ...rest,
    'without-steppers': !steppers,
  })}>${slot}</wa-number-input>`;
}

/** An amount: decimal keyboard, the currency symbol before it («Q», «US$»). */
export function moneyControl(a: Attrs & Labelled & { currency?: string }): string {
  const { currency = 'Q', ...b } = a;
  const { rest, extra, slot } = labelAttrs(b);
  return `<wa-input${attrs({ inputmode: 'decimal', autocomplete: 'off', ...extra, ...rest })}>${slot}<span slot="start" class="affix" aria-hidden="true">${esc(currency)}</span></wa-input>`;
}

/** Several lines of text: `<wa-textarea>` (its text is the `value`). */
export function textareaControl(a: Attrs & Labelled): string {
  const { rest, extra, slot } = labelAttrs(a);
  return `<wa-textarea${attrs({ resize: 'vertical', ...extra, ...rest })}>${slot}</wa-textarea>`;
}

/** One option of a select. */
export interface SelectOption {
  value: string;
  label: string;
  selected?: boolean;
  disabled?: boolean;
}

/** An option's markup (for lists built elsewhere and passed to `selectControl` as HTML). */
export const optionTag = (value: string, label: string, selected = false, disabled = false) =>
  `<wa-option${attrs({ value, selected, disabled })}>${esc(label)}</wa-option>`;

/**
 * A select: `<wa-select>` with `<wa-option>`s. As a native select, the first option is chosen
 * when none is marked `selected` (unless a `placeholder` says «nothing chosen yet»).
 */
export function selectControl(a: Attrs & Labelled, options: SelectOption[] | string): string {
  const { rest, extra, slot } = labelAttrs(a);
  let html =
    typeof options === 'string'
      ? options
      : options.map((o) => optionTag(o.value, o.label, o.selected, o.disabled)).join('');
  if (!rest.placeholder && !rest.multiple && !/<wa-option\b[^>]*\sselected[\s>]/.test(html))
    html = html.replace(/<wa-option\b(?![^>]*\sdisabled[\s>])/, '<wa-option selected');
  // The chosen value also goes on the select itself, so `.value` answers it as soon as the
  // markup is on the page (the component reads its options a moment later).
  const chosen = rest.multiple
    ? undefined
    : [...html.matchAll(/<wa-option\b[^>]*>/g)]
        .map((m) => m[0])
        .find((tag) => /\sselected[\s>]/.test(tag))
        ?.match(/\svalue="([^"]*)"/)?.[1];
  const value = rest.value ?? (chosen === undefined ? undefined : unesc(chosen));
  return `<wa-select${attrs({ ...extra, ...rest, value })}>${slot}${html}</wa-select>`;
}

/** A checkbox with its label (HTML, already escaped by the caller): `<wa-checkbox>`. */
export const checkboxControl = (a: Attrs, labelHtml: string) =>
  `<wa-checkbox${attrs(a)}>${labelHtml}</wa-checkbox>`;

/** An on/off setting that applies at once: `<wa-switch>`. */
export const switchControl = (a: Attrs, labelHtml: string) =>
  `<wa-switch${attrs(a)}>${labelHtml}</wa-switch>`;

/** One choice of a radio group. */
export interface RadioOption {
  value: string;
  /** The choice's label (HTML, already escaped by the caller). */
  labelHtml: string;
  disabled?: boolean;
  /** Other attributes of the choice (`class: 'check'` draws it as a box). */
  attrs?: Attrs;
}

/**
 * A radio group: one form value (`name`, `value`, `required`) and its choices. Its `change`
 * comes from the group, whose `value` is the chosen one ('' while none).
 */
export function radioControl(a: Attrs & Labelled, radios: RadioOption[]): string {
  const { rest, extra, slot } = labelAttrs(a);
  return `<wa-radio-group${attrs({ ...extra, ...rest })}>${slot}${radios
    .map(
      (r) =>
        `<wa-radio${attrs({ value: r.value, disabled: r.disabled, ...r.attrs })}>${r.labelHtml}</wa-radio>`,
    )
    .join('')}</wa-radio-group>`;
}

/**
 * The anti-bot field of a public form: a native input nobody sees (bots fill every plain
 * input they find; people never reach it). It is the one bare text input on purpose.
 */
export const honeypot = (name: string) =>
  `<label>No llenes este campo <input name="${esc(name)}" tabindex="-1" autocomplete="off" /></label>`;

/** The tags of the layer's controls (what `bindControls` and the source check know). */
export const CONTROL_TAGS = [
  'wa-input',
  'wa-number-input',
  'wa-textarea',
  'wa-select',
  'wa-checkbox',
  'wa-switch',
  'wa-radio-group',
] as const;

const STARTS_WITH_CONTROL = new RegExp(`^\\s*<(${CONTROL_TAGS.join('|')})\\b`);

/**
 * Gives a control built by this module its label when the label comes from around it
 * (`field(label, control)` in the console): plain text of the label's HTML, unless the
 * control already has one. Any other markup is returned as it came. A `required` field gets
 * the label with its mark (in the label slot) and the `required` attribute.
 */
export function withLabel(
  control: string,
  labelHtml: string,
  opts: { required?: boolean } = {},
): string {
  const m = control.match(STARTS_WITH_CONTROL);
  if (!m) return control;
  const start = control.slice(0, control.indexOf('>') + 1);
  if (/\slabel="/.test(start) || control.includes('slot="label"')) return control;
  if (opts.required) {
    const tag = /\srequired[\s>=]/.test(start)
      ? start
      : start.replace(/^(\s*<[\w-]+)/, '$1 required');
    return `${tag}<span slot="label">${labelHtml} ${REQUIRED_MARK}</span>${control.slice(start.length)}`;
  }
  const text = labelHtml
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return control.replace(STARTS_WITH_CONTROL, (tag) => `${tag} label="${text}"`);
}

/** Whether markup is (starts with) one of the layer's controls. */
export const isControl = (html: string) => STARTS_WITH_CONTROL.test(html);

/** The values of the checked checkboxes under `root` that match `selector` (`:checked`). */
export const checkedValues = (root: ParentNode, selector: string) =>
  [...root.querySelectorAll<HTMLElement & { checked: boolean; value: string }>(selector)]
    .filter((el) => el.checked)
    .map((el) => el.value);
